import { useCallback, useState } from 'react';

/**
 * Hook for managing confirmation dialog state and callbacks.
 * Provides a simple API for showing/hiding dialogs and handling user responses.
 *
 * @param options - Configuration options
 * @param options.onConfirm - Callback when user confirms the action
 * @param options.onCancel - Optional callback when user cancels
 *
 * @returns Object containing dialog state and control methods
 *
 * @example
 * ```tsx
 * const dialog = useConfirmationDialog({
 *   onConfirm: () => {
 *     // Handle confirmation
 *     blocker.proceed();
 *   },
 *   onCancel: () => {
 *     // Handle cancellation
 *     blocker.reset();
 *   },
 * });
 *
 * // Show dialog
 * <Button onClick={dialog.show}>Delete</Button>
 *
 * // Render dialog
 * <ConfirmationDialog
 *   isOpen={dialog.isOpen}
 *   onClose={dialog.handleCancel}
 *   onConfirm={dialog.handleConfirm}
 * />
 * ```
 */
export function useConfirmationDialog({
  onConfirm,
  onCancel,
}: {
  /**
   * `false` intors EXPLICIT tine dialogul deschis (actiunea a esuat si se poate reincerca din el).
   * Orice altceva — inclusiv `undefined`, adica toti apelantii de dinainte — il inchide.
   */
  onConfirm: () => void | boolean | Promise<void | boolean>;
  onCancel?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Stabile: `setIsOpen` e stabil, deci si astea pot fi. Fara `useCallback`,
  // hook-ul intorcea functii noi la fiecare randare, si orice `useCallback` din
  // consumator care le punea in deps nu memoiza nimic.
  const show = useCallback(() => setIsOpen(true), []);
  const hide = useCallback(() => setIsOpen(false), []);

  const handleConfirm = async () => {
    const rezultat = await onConfirm();
    if (rezultat === false) return;
    hide();
  };

  const handleCancel = () => {
    onCancel?.();
    hide();
  };

  return {
    isOpen,
    show,
    hide,
    handleConfirm,
    handleCancel,
  };
}
